import React from 'react';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { WarningCircleIcon } from '@phosphor-icons/react';

interface SupplierChangeConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentSupplierName: string;
  newSupplierName: string;
  loading?: boolean;
}

/**
 * Confirmation modal for changing supplier on draft rebate agreements
 * Warns user that this will reprocess the entire agreement
 */
export function SupplierChangeConfirmationModal({
  open,
  onClose,
  onConfirm,
  currentSupplierName,
  newSupplierName,
  loading = false,
}: SupplierChangeConfirmationModalProps) {
  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      title="Change Supplier?"
      confirmText="Change Supplier"
      cancelText="Cancel"
      loading={loading}
      dangerous={true}
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
          <WarningCircleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold mb-1">
              This will reprocess the entire agreement
            </p>
            <p>
              Changing the supplier will reprocess this agreement with the new
              supplier context. All rebate rules and associations will be
              re-extracted.
            </p>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Current Supplier:</span>
            <span className="font-medium">{currentSupplierName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">New Supplier:</span>
            <span className="font-medium text-blue-600">{newSupplierName}</span>
          </div>
        </div>

        <div className="text-xs text-gray-500">
          This action cannot be undone. The old agreement and all its data will
          be replaced.
        </div>
      </div>
    </ConfirmDialog>
  );
}
